import { createRouter, createWebHistory } from "vue-router";

import Login from "@/views/account/Login.vue";
import ChangePassword from "@/views/account/ChangePassword.vue";
import SetPassword from "@/views/account/SetPassword.vue";
import ResetPassword from "@/views/account/ResetPassword.vue";

import Dashboard from "@/views/Dashboard.vue";
import Chat from "@/views/chats/Chat.vue";
import Dispatchers from "@/views/dispatchers/Dispatchers.vue";

import Facilities from "@/views/companies/facilities/Facilities.vue";
import FacilitiesEdit from "@/views/companies/facilities/Edit.vue";

import Coordinators from "@/views/coordinators/Coordinators.vue";
import Owners from "@/views/owners/Owners.vue";
import Drivers from "@/views/drivers/Drivers.vue";
import UserEdit from "@/views/users/UserEdit.vue";
import UserView from "@/views/users/UserView.vue";

import Trucks from "@/views/trucks/Trucks.vue";
import TruckView from "@/views/trucks/TruckView.vue";
import TrucksSearch from "@/views/trucks/TrucksSearch.vue";
import TruckEdit from "@/views/trucks/edit/TruckEdit.vue";
import Logs from "@/views/settings/Logs.vue";
import TrucksHistory from "@/views/trucks/history/TrucksHistory.vue";
import MapPosition from "@/views/misc/MapPosition.vue";
import Profile from "@/views/account/Profile.vue";
import Orders from "@/views/orders/Orders.vue";
import OrderEdit from "@/views/orders/OrderEdit.vue";
import TripEdit from "@/views/trips/edit/TripEdit.vue";
import CompanyProfile from "@/views/companies/CompanyProfile.vue";
import Carriers from "@/views/companies/carriers/Carriers.vue";
import CarriersEdit from "@/views/companies/carriers/Edit.vue";
import Customers from "@/views/companies/customers/Customers.vue";
import CustomerEdit from "@/views/companies/customers/Edit.vue";
import { UnauthorizedGuard } from "./guards/unauthorized.guard";
import { AuthorizedGuard } from "./guards/authorized.guard";
import NotFound from "@/views/misc/NotFound.vue";
import OrderView from "@/views/orders/OrderView.vue";
import TripView from "@/views/trips/view/TripView.vue";
import TripOverviewTab from "@/views/trips/view/TripOverviewTab.vue";
import TripCheckCallsTab from "@/views/trips/view/TripCheckCallsTab.vue";
import TripCheckCallsRouteTab from "@/views/trips/view/TripCheckCallsRouteTab.vue";
import TripBrokerUpdatesTab from "@/views/trips/view/TripBrokerUpdatesTab.vue";
import TripBrokerUpdatesOrderTab from "@/views/trips/view/TripBrokerUpdatesOrderTab.vue";
import TripMonitor from "@/views/trips/monitor/TripMonitor.vue";
import BillingReports from "@/views/billing/reports/BillingReports.vue";
import DispatcherEdit from "@/views/dispatchers/view/DispatcherEdit.vue";
import DispatcherCreate from "@/views/dispatchers/view/DispatcherCreate.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto.ts";

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/", redirect: "/dashboard", name: "index" },

    /// Security
    {
      path: "/login",
      component: Login,
      name: "login",
      meta: { layout: "blank" },
      beforeEnter: [UnauthorizedGuard],
    },
    {
      path: "/change-password",
      component: ChangePassword,
      name: "change-password",
      beforeEnter: [UnauthorizedGuard],
    },
    {
      path: "/reset-password",
      component: ResetPassword,
      name: "reset-password",
      meta: { layout: "blank" },
      beforeEnter: [UnauthorizedGuard],
    },
    {
      path: "/dispatchers/:id/set-password",
      component: SetPassword,
      name: "set-password",
      meta: { layout: "blank" },
      beforeEnter: [UnauthorizedGuard],
    },

    /// Dashboard
    {
      path: "/dashboard",
      component: Dashboard,
      name: "dashboard",
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/account/profile",
      component: Profile,
      name: "account-profile",
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/chat",
      component: Chat,
      name: "chat",
      meta: { permission: DispatcherPermissionValue.ViewChat },
      beforeEnter: [AuthorizedGuard],
    },

    /// Orders
    {
      path: "/orders",
      component: Orders,
      name: "orders",
      meta: { permission: DispatcherPermissionValue.ViewOrders },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/orders/create",
      component: OrderEdit,
      name: "order-create",
      meta: { permission: DispatcherPermissionValue.EditOrCreateOrder },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/orders/:id/edit",
      component: OrderEdit,
      name: "order-edit",
      meta: { permission: DispatcherPermissionValue.EditOrCreateOrder },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/orders/:id",
      component: OrderView,
      name: "order-view",
      meta: { permission: DispatcherPermissionValue.ViewOrderInfo },
      beforeEnter: [AuthorizedGuard],
    },

    /// Trips
    {
      path: "/trips/monitor",
      component: TripMonitor,
      name: "trip-monitor",
      meta: { permission: DispatcherPermissionValue.ViewTripMonitor },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/trips/:id",
      component: TripEdit,
      name: "trip-edit",
      meta: { permission: DispatcherPermissionValue.EditOrCreateTrip },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/trips/:id/view",
      component: TripView,
      name: "trip-view-base",
      meta: { permission: DispatcherPermissionValue.ViewTripInfo },
      beforeEnter: [AuthorizedGuard],
      children: [
        {
          path: "overview",
          name: "trip-view",
          component: TripOverviewTab,
          strict: false,
        },
        {
          path: "check-calls",
          name: "trip-check-calls",
          component: TripCheckCallsTab,
          meta: { permission: DispatcherPermissionValue.ViewCheckCalls },
          children: [
            {
              path: "routes/:routeId",
              name: "trip-check-calls-route",
              component: TripCheckCallsRouteTab,
              meta: { permission: DispatcherPermissionValue.ViewCheckCalls },
            },
          ],
        },
        {
          path: "broker-updates",
          name: "trip-broker-updates",
          component: TripBrokerUpdatesTab,
          meta: { permission: DispatcherPermissionValue.ViewBrokerUpdates },
          children: [
            {
              path: "order/:orderId",
              name: "trip-broker-updates-order",
              component: TripBrokerUpdatesOrderTab,
              meta: { permission: DispatcherPermissionValue.ViewBrokerUpdates },
            },
          ],
        },
      ],
    },

    /// Users
    {
      path: "/users/drivers",
      component: Drivers,
      name: "drivers",
      meta: { permission: DispatcherPermissionValue.ViewUsers },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/users/owners",
      component: Owners,
      name: "owners",
      meta: { permission: DispatcherPermissionValue.ViewUsers },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/users/coordinators",
      component: Coordinators,
      name: "coordinators",
      meta: { permission: DispatcherPermissionValue.ViewUsers },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/users/create",
      component: UserEdit,
      name: "user-create",
      meta: { permission: DispatcherPermissionValue.EditOrCreateUser },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/users/:id(\\d+)/edit",
      component: UserEdit,
      name: "user-edit",
      meta: { permission: DispatcherPermissionValue.EditOrCreateUser },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/users/:id(\\d+)/view",
      component: UserView,
      name: "user-view",
      meta: { permission: DispatcherPermissionValue.ViewUserInfo },
      beforeEnter: [AuthorizedGuard],
    },

    /// Company
    {
      path: "/company/profile",
      component: CompanyProfile,
      name: "company-profile",
      meta: { permission: DispatcherPermissionValue.EditOrCreateCompany },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/company/carriers",
      component: Carriers,
      name: "carriers",
      meta: { permission: DispatcherPermissionValue.ViewCompanies },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/company/carriers/:id/edit",
      component: CarriersEdit,
      name: "carriers-edit",
      meta: { permission: DispatcherPermissionValue.EditOrCreateCompany },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/company/customers",
      component: Customers,
      name: "customers",
      meta: { permission: DispatcherPermissionValue.ViewCompanies },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/company/customers/:id/edit",
      component: CustomerEdit,
      name: "customer-edit",
      meta: { permission: DispatcherPermissionValue.EditOrCreateCompany },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/company/facilities",
      component: Facilities,
      name: "facilities",
      meta: { permission: DispatcherPermissionValue.ViewCompanies },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/company/facilities/create",
      component: FacilitiesEdit,
      name: "facilities-create",
      meta: { permission: DispatcherPermissionValue.EditOrCreateCompany },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/company/facilities/:id/edit",
      component: FacilitiesEdit,
      name: "facilities-edit",
      meta: { permission: DispatcherPermissionValue.EditOrCreateCompany },
      beforeEnter: [AuthorizedGuard],
    },

    // Trucks
    {
      path: "/fleets/trucks",
      component: Trucks,
      name: "trucks",
      meta: { permission: DispatcherPermissionValue.ViewTrucks },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/fleets/history",
      component: TrucksHistory,
      name: "trucks-history",
      meta: { permission: DispatcherPermissionValue.ViewTrucks },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/fleets/search",
      component: TrucksSearch,
      name: "trucks-search",
      meta: { permission: DispatcherPermissionValue.AccessToTruckSearch },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/fleets/trucks/:id",
      component: TruckView,
      name: "truck-view",
      meta: { permission: DispatcherPermissionValue.ViewTruckInfo },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/fleets/trucks/:id/edit",
      component: TruckEdit,
      name: "truck-edit",
      meta: { permission: DispatcherPermissionValue.EditOrCreateTruck },
      beforeEnter: [AuthorizedGuard],
    },

    {
      path: "/dispatchers",
      component: Dispatchers,
      name: "dispatchers",
      meta: { permission: DispatcherPermissionValue.ViewDispatchers },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/dispatcher/create",
      component: DispatcherCreate,
      name: "dispatcher-create",
      meta: { permission: DispatcherPermissionValue.EditOrCreateDispatcher },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/dispatcher/edit/:id",
      component: DispatcherEdit,
      name: "dispatcher-edit",
      meta: { permission: DispatcherPermissionValue.EditOrCreateDispatcher },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/logs",
      component: Logs,
      name: "logs",
      beforeEnter: [AuthorizedGuard],
    },

    /// Service
    {
      path: "/map/position",
      component: MapPosition,
      name: "map-position",
      beforeEnter: [AuthorizedGuard],
      meta: { layout: "blank" },
    },

    /// Billing
    {
      path: "/billing/reports",
      component: BillingReports,
      name: "billing-reports",
      beforeEnter: [AuthorizedGuard],
    },

    // Fallback
    { path: "/:pathMatch(.*)*", name: "not-found", component: NotFound },
  ],
});
